//-- Copyright (c) 2021 TMT Insights Group, LLC
import { copyToClipboard } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Alert, Button, PopperProps, Portal, Snackbar, Typography } from '@mui/material';
import React from 'react';
import ConditionalTooltip from '../ConditionalTooltip/ConditionalTooltip';

export type ClickCopyProps = {
  children: React.ReactNode;
  copyText?: string;
  placement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  noBackground?: boolean;
  disableTextClick?: boolean;
  fullWidth?: boolean;
};

const ClickCopy = ({
  children = null,
  copyText,
  placement = 'top',
  noBackground = false,
  disableTextClick = false,
  fullWidth = false,
  ...props
}: ClickCopyProps) => {
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const copyRef = React.useRef<HTMLSpanElement>(null);

  const onCopyClick = (e: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>) => {
    e.stopPropagation();
    const renderedText = (copyRef.current?.innerText || copyRef.current?.textContent || '').replace(/[\r\n]+/g, ' ').trim();
    const textToCopy = copyText || renderedText;

    if (!textToCopy) return;

    setOpenSnackbar(true);
    copyToClipboard(textToCopy);
  };

  const closeSnackbar = (e: React.SyntheticEvent | Event | null) => {
    e?.stopPropagation();
    setOpenSnackbar(false);
  };

  if (!children) return null;
  return (
    <span style={{ width: fullWidth ? '100%' : undefined }} {...props}>
      <Portal>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openSnackbar} autoHideDuration={2000} onClose={closeSnackbar}>
          <Alert onClose={closeSnackbar} severity="info" sx={{ width: '100%' }} icon={<PolarisIcon icon="copy" />}>
            Copied to clipboard!
          </Alert>
        </Snackbar>
      </Portal>
      <ConditionalTooltip
        arrow
        show={!!navigator?.clipboard}
        leaveDelay={200}
        isNewTooltip
        PopperProps={popperProps}
        title={
          <Button size="small" onClick={onCopyClick}>
            <PolarisIcon icon="copy" height={18} style={{ opacity: 0.8 }} />
            <Typography variant="subtitle2" sx={{ ml: 0.5 }}>
              Click to copy
            </Typography>
          </Button>
        }
        placement={placement}
        sx={{ width: fullWidth ? '100%' : undefined }}
      >
        <CopyText ref={copyRef} fullWidth={fullWidth ? 1 : 0} onClick={disableTextClick ? undefined : onCopyClick} noBackground={+noBackground}>
          {children}
        </CopyText>
      </ConditionalTooltip>
    </span>
  );
};

export default ClickCopy;

const CopyText = styled.span<{ noBackground: number; fullWidth: number }>`
  position: relative;
  display: inline-block;
  padding: 3px 5px;
  margin-left: -5px;
  line-height: 1.4rem;
  border-radius: 5px;
  word-break: break-word;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : undefined)};
  &:hover {
    cursor: pointer;
    ${({ noBackground, theme }) => !noBackground && `background-color: ${theme.palette.action.hover};`}
  }
`;

const popperProps: Partial<PopperProps> = {
  sx: {
    '& .MuiTooltip-tooltip.MuiTooltip-tooltip': {
      mr: 1,
      p: 0,
      '& .MuiTypography-root': {
        fontSize: '.9rem',
        fontWeight: 500
      }
    },
    '& .MuiButtonBase-root': {
      p: 0.75,
      color: 'inherit',
      background: 'none'
    }
  },
  disablePortal: false
};
